import {
  type RefCallback,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type UseAutoHeightOptions = {
  /** Pixels subtracted from the observed height (inner padding, borders, etc.). */
  offset?: number;
  /** Used when the element has not been measured yet (height 0). */
  fallbackHeight?: number;
  /** When false, observers are not attached. */
  enabled?: boolean;
};

/**
 * Observes an element’s height with `ResizeObserver` so scroll regions (e.g. tables)
 * can match the space between layout chrome (header/footer) and the viewport.
 * Uses a callback ref so remounts always rebind measurement.
 */
export function useAutoHeight<T extends HTMLElement = HTMLDivElement>(
  options: UseAutoHeightOptions = {},
): { ref: RefCallback<T | null>; height: number } {
  const { offset = 0, fallbackHeight = 320, enabled = true } = options;
  const [element, setElement] = useState<T | null>(null);
  const [height, setHeight] = useState(fallbackHeight);
  const offsetRef = useRef(offset);
  const fallbackRef = useRef(fallbackHeight);
  offsetRef.current = offset;
  fallbackRef.current = fallbackHeight;

  const ref = useCallback<RefCallback<T | null>>((node) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!enabled || !element) return;

    const measure = () => {
      const h = element.getBoundingClientRect().height;
      const next = Math.max(0, Math.floor(h - offsetRef.current));
      setHeight(next > 0 ? next : fallbackRef.current);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(element);
    return () => ro.disconnect();
  }, [enabled, element]);

  return { ref, height };
}
